import { useEffect, useMemo, useRef } from 'react';
import { useChat, useRoomContext, useTranscriptions, } from '@livekit/components-react';
import { batchPersistMessages } from '@/lib/session-storage';
import { getTracker } from '@/lib/tracker';
import { transcriptionToChatMessage } from '@/lib/utils';
export default function useChatAndTranscription(options) {
    const transcriptions = useTranscriptions();
    const chat = useChat();
    const room = useRoomContext();
    const trackedIds = useRef(new Set());
    const messagesRef = useRef([]);
    // Convert restored messages (sessionStorage) into ReceivedChatMessage format
    const restoredChatMessages = useMemo(() => {
        return ((options === null || options === void 0 ? void 0 : options.restoredMessages) || []).map((msg) => ({
            id: msg.id,
            timestamp: msg.timestamp,
            message: msg.message,
            from: msg.isLocal ? room.localParticipant : undefined,
        }));
    }, [options === null || options === void 0 ? void 0 : options.restoredMessages, room.localParticipant]);
    // Convert DB messages (conversation history) into ReceivedChatMessage format
    const dbChatMessages = useMemo(() => {
        return ((options === null || options === void 0 ? void 0 : options.dbMessages) || []).map((msg) => ({
            id: msg.id,
            timestamp: msg.timestamp,
            message: msg.message,
            from: msg.isLocal ? room.localParticipant : undefined,
        }));
    }, [options === null || options === void 0 ? void 0 : options.dbMessages, room.localParticipant]);
    const mergedTranscriptions = useMemo(() => {
        const liveMessages = [
            ...transcriptions.map((t) => transcriptionToChatMessage(t, room)),
            ...chat.chatMessages,
        ];
        // Merge all sources, deduplicating by ID.
        // Process LIVE messages first so the latest streaming content wins
        // over stale restored/DB versions of the same message.
        const seen = new Map();
        for (const msg of [...liveMessages, ...restoredChatMessages, ...dbChatMessages]) {
            if (!seen.has(msg.id)) {
                seen.set(msg.id, msg);
            }
        }
        return Array.from(seen.values()).sort((a, b) => a.timestamp - b.timestamp);
    }, [transcriptions, chat.chatMessages, room, restoredChatMessages, dbChatMessages]);
    // Keep a ref to the latest messages for the beforeunload handler
    messagesRef.current = mergedTranscriptions;
    const flushTimer = useRef(null);
    // Helper: serialize all current messages into the format sessionStorage expects
    const serializeAll = (msgs) => msgs.map((msg) => {
        var _a, _b;
        return ({
            id: msg.id,
            timestamp: msg.timestamp,
            message: msg.message,
            isLocal: (_b = (_a = msg.from) === null || _a === void 0 ? void 0 : _a.isLocal) !== null && _b !== void 0 ? _b : false,
        });
    });
    // Debounced batch-persist: waits 500ms after the last stream update before
    // writing to sessionStorage. During fast streaming this fires at most 2×/sec
    // instead of on every chunk — one read + one write per flush.
    useEffect(() => {
        var _a, _b;
        // Track new user messages immediately (cheap, no I/O)
        for (const msg of mergedTranscriptions) {
            if (!trackedIds.current.has(msg.id)) {
                trackedIds.current.add(msg.id);
                if ((_a = msg.from) === null || _a === void 0 ? void 0 : _a.isLocal) {
                    (_b = getTracker()) === null || _b === void 0 ? void 0 : _b.track('chat_message_sent');
                }
            }
        }
        // Schedule a debounced persist
        if (flushTimer.current)
            clearTimeout(flushTimer.current);
        flushTimer.current = setTimeout(() => {
            batchPersistMessages(serializeAll(messagesRef.current), options === null || options === void 0 ? void 0 : options.widgetId);
        }, 500);
        return () => {
            if (flushTimer.current)
                clearTimeout(flushTimer.current);
        };
    }, [mergedTranscriptions, options === null || options === void 0 ? void 0 : options.widgetId]);
    // Final persist on page unload — catches any streaming content
    // the debounce timer hasn't flushed yet. Single batch write.
    useEffect(() => {
        const onBeforeUnload = () => {
            if (flushTimer.current)
                clearTimeout(flushTimer.current);
            batchPersistMessages(serializeAll(messagesRef.current), options === null || options === void 0 ? void 0 : options.widgetId);
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [options === null || options === void 0 ? void 0 : options.widgetId]);
    return { messages: mergedTranscriptions, send: chat.send };
}
